// Agregação de SLA de atendimento (SlaAtendimentoBlock.tsx) — extraída pra ficar
// testável fora do componente, seguindo o padrão de
// views/CRM/Analytics/rankingConsultores.ts: o componente só busca dado e chama
// esta função; toda a soma/média/ordenação mora aqui.
//
// Mora em src/lib/ (não em views/CRM/Analytics/blocks/, onde nasceu em 09/2026)
// porque virou dependência do motor de fechamento do Comercial Interno
// (comissaoInternaV1.ts, que precisa da mesma média de SLA útil pro bônus de
// R$100) — e src/lib não pode importar de dentro de views/ sem inverter a
// direção que o projeto usa em todo o resto (CLAUDE.md: "views/ tem as telas
// por módulo; src/ tem só o compartilhado").
import { tempoUtilDecorrido } from './escalaDisponibilidade.ts';
import type { HorarioTrabalho, AusenciaTrabalho, NegociacaoAtribuicao } from '../../types.ts';

/** Evita que 1 lead isolado vire "melhor SLA da equipe" por sorte de amostra. */
export const MIN_LEADS_RANKING = 3;

export interface RankingLinha {
    responsavelId: string;
    nome: string;
    leads: number;
    mediaBrutoMs: number;
    mediaUtilMs: number;
    /** Quantos desses leads caíram fora do horário de expediente de todo mundo (fallback do rodízio). */
    caiuIndisp: number;
}

/** Uma negociação individual, com o SLA já calculado — base do drill-down que
 *  deixa achar o outlier (ex.: "essa foi 70min, as outras 5-15min") e marcar
 *  `sla_desconsiderado`. Inclui as desconsideradas também (com o tempo delas
 *  visível), pra dar pra conferir/reverter uma correção manual. */
export interface DetalheItem {
    id: string;
    negociacaoId: number;
    responsavelId: string;
    nome: string;
    clienteNome: string | null;
    atribuidoEm: string;
    brutoMs: number;
    utilMs: number;
    caiuIndisponibilidade: boolean;
    slaDesconsiderado: boolean;
    slaDesconsideradoMotivo: string | null;
}

export interface SlaAgregado {
    mediaBrutoGeral: number | null;
    mediaUtilGeral: number | null;
    ranking: RankingLinha[];
    /** Todas as concluídas do período, uma por negociação — inclui as desconsideradas. */
    itens: DetalheItem[];
}

/**
 * Bruto = tempo corrido entre atribuir o lead e a primeira atividade humana.
 * Útil = o mesmo intervalo, mas só contando o horário de expediente de cada
 * responsável (tempoUtilDecorrido, por-pessoa).
 *
 * Só entram nas médias/ranking as atribuições já concluídas — quem chama filtra
 * `primeira_atividade_em` antes (uma ainda aberta não tem duração "concluída").
 * `sla_desconsiderado` (correção manual de adm/gerente, ex.: um teste que gerou
 * tempo atípico) fica fora das médias/ranking, mas ainda aparece em `itens`
 * (com o tempo calculado) — é esta função que decide o que entra na conta, não
 * quem chama.
 */
export function computeSlaAgregado(
    concluidasComDesconsideradas: NegociacaoAtribuicao[],
    horariosPorPessoa: Map<string, HorarioTrabalho[]>,
    ausenciasPorPessoa: Map<string, AusenciaTrabalho[]>,
    perfisPorId: Map<string, string | null | undefined>,
    clienteNomePorNegociacao?: Map<number, string | null | undefined>,
): SlaAgregado {
    let somaBruto = 0;
    let somaUtil = 0;
    let consideradas = 0;
    const porPessoa = new Map<string, { somaBruto: number; somaUtil: number; leads: number; caiuIndisp: number }>();
    const itens: DetalheItem[] = [];

    for (const a of concluidasComDesconsideradas) {
        const inicio = new Date(a.atribuido_em);
        const fim = new Date(a.primeira_atividade_em!);
        const bruto = Math.max(0, fim.getTime() - inicio.getTime());
        const util = tempoUtilDecorrido(horariosPorPessoa.get(a.responsavel_id) || [], ausenciasPorPessoa.get(a.responsavel_id) || [], inicio, fim);

        itens.push({
            id: a.id,
            negociacaoId: a.negociacao_id,
            responsavelId: a.responsavel_id,
            nome: perfisPorId.get(a.responsavel_id) || 'Sem nome',
            clienteNome: clienteNomePorNegociacao?.get(a.negociacao_id) || null,
            atribuidoEm: a.atribuido_em,
            brutoMs: bruto,
            utilMs: util,
            caiuIndisponibilidade: a.caiu_por_indisponibilidade,
            slaDesconsiderado: a.sla_desconsiderado,
            slaDesconsideradoMotivo: a.sla_desconsiderado_motivo ?? null,
        });

        if (a.sla_desconsiderado) continue; // fora das médias/ranking, mas já registrado em itens acima

        consideradas += 1;
        somaBruto += bruto;
        somaUtil += util;

        const agg = porPessoa.get(a.responsavel_id) || { somaBruto: 0, somaUtil: 0, leads: 0, caiuIndisp: 0 };
        agg.somaBruto += bruto;
        agg.somaUtil += util;
        agg.leads += 1;
        if (a.caiu_por_indisponibilidade) agg.caiuIndisp += 1;
        porPessoa.set(a.responsavel_id, agg);
    }

    const ranking: RankingLinha[] = Array.from(porPessoa.entries())
        .filter(([, v]) => v.leads >= MIN_LEADS_RANKING)
        .map(([responsavelId, v]) => ({
            responsavelId,
            nome: perfisPorId.get(responsavelId) || 'Sem nome',
            leads: v.leads,
            mediaBrutoMs: v.somaBruto / v.leads,
            mediaUtilMs: v.somaUtil / v.leads,
            caiuIndisp: v.caiuIndisp,
        }))
        .sort((a, b) => a.mediaUtilMs - b.mediaUtilMs); // menor é melhor

    return {
        mediaBrutoGeral: consideradas > 0 ? somaBruto / consideradas : null,
        mediaUtilGeral: consideradas > 0 ? somaUtil / consideradas : null,
        ranking,
        itens,
    };
}
